const helpText = ({enneaName,imfName,fullName,enneaTeamName,imfTeamName,fullTeamName,openModelCard}) =>  {
return (
<div className="aephoriaHelpText">

<p>See what's <a href="#whatsnew" id="urlLink">new!</a></p>

<h2>Introduction</h2>

<p>
This help page gives an overview for using the Coach Portal.
</p>

<p>
The Coach Portal allows you to add assessments, monitor and guide clients through assessment completion, provide feedback to the clients in the form of Aephoria assessment reports, and group assessments into teams to obtain Aephoria team reports.
</p>


{/*
<p>
FAQ What are the enneagram / IMF / full assessments? 
</p>

<p>
FAQ What is the difference between Enneagram and IMf?
</p>
*/}

<p>Aephoria offers three assessments:</p>
<ul>
    <li>{enneaName} (enneagram only at a cost of 1 credit)</li>
    <li>{imfName} (maturity only at a cost of 2 credits)</li>
    <li>{fullName} (enneagram + maturity combined into one assessment at a cost of 3 credits)</li>
</ul>


<p>
So a client might first do an {enneaName} assessment, and later an {imfName} assessment. Or, first a {fullName} assessment, and then later, to determine the effect of an intervention and to reassess the Maturity component, a {imfName} assessment.
</p>

<p>
A group of clients may work together as a team. The portal allows you to identify this team, add client assessments to the team, and then request an Aephoria team report for the team. There are three different team reports based on the common assessment ({enneaName}, {imfName}, {fullName}) that the team members have completed.
</p>

<h3>Video tutorials</h3>

<p>
These short tutorial videos walk through the Coach Portal section by section:
</p>

<ul>
    <li><a href="https://www.youtube.com/watch?v=d9VI9g57tDU&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Logging In, Dashboard Overview, Profile Settings &amp; Portal Administration</u></a>: logging in, navigating the dashboard, the main menu, where to find your assessment balance, an overview of portal sections, tips for getting started, updating your profile, changing your password, adding or removing assistant access, managing notifications, reviewing policies and agreements, configuring email notifications, and best practice for securely managing assistants and protecting client information.</li>
    <li><a href="https://www.youtube.com/watch?v=vpoC7y_Oz-Y&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Managing Clients &amp; Sending Assessments</u></a>: adding and editing clients, assigning an assessment, updating email templates, sending invitation links, sending reminders, reading status indicators, importing/converting 3rd party reports.</li>
    <li><a href="https://www.youtube.com/watch?v=s_ZCS5tulU0&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Reports &amp; Coach Collaboration</u></a>: downloading client reports, sending reports directly from the portal, choosing which reports to send, sharing via Secondary Coach Ownership, revoking access once a project ends.</li>
    <li><a href="https://www.youtube.com/watch?v=7RNAxsNek2Y&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Professional Pairs and Couples</u></a>: adding a pair from existing client details, assigning an assessment, generating the pair request, reading status indicators, and sending the report.</li>
    <li><a href="https://www.youtube.com/watch?v=CUq1eiFXwcw&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Working With Teams</u></a>: creating a team, adding and removing members, assigning team leaders, generating Team Reports, downloading PDF/PowerPoint Team Reports and Team Summary spreadsheets.</li>
    <li><a href="https://www.youtube.com/watch?v=RqUxvkgKiuI&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Credits &amp; Billing</u></a>: how assessment credits work, your credit balance, transaction history, pay-as-you-go billing, ordering credits in bulk, bulk discounts, cancelling a bulk order, and tracking monthly usage against individual and team report costs.</li>
    <li><a href="https://www.youtube.com/watch?v=ybGSKW665vc&list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>Resources, Messages &amp; Help</u></a>: sample reports, client brochures, portal messages and updates, FAQs, help documentation, training videos, new feature announcements, and where to download marketing resources to share with clients.</li>
</ul>

<p>
Or see the full <a href="https://www.youtube.com/playlist?list=PLWA47UiOOB-c" target="_blank" rel="noopener noreferrer"><u>tutorial playlist</u></a> on YouTube.
</p>

<h2>Logging in and profile management</h2>

<p>
You have logged in with your email address and password provided to you by Aephoria. You can change your password and personal and company details by clicking on your name in the top-left corner of the portal - this takes you to your user profile. Here you can also enable two-factor authentication, specify assistants, review your agreements and policies with Aephoria, set notification preferences, and access web resources including your accreditation stamps and a link to give to your clients to allow them to self-register for the Aephoria enneagram assessment.
</p>

<p>
{/*FAQ*/}
Can one account be used by more than one coach?
Coaches in an organization might work together on all the organization's clients. You can add coaches as assistants, or assign assessments to coaches via the Secondary Coach Ownership functionality, but these aspects are not discussed further in this help page.
</p>

<h2>Typical lifecycle of a project</h2>

<p>
Let's go through a typical lifecycle of a project, adding assessments, inviting and monitoring the clients through the assessments, and providing feedback to the clients with Aephoria assessment reports.
</p>

<h3>Adding initial assessments</h3>

<p>There are three ways of adding new clients / assessments:</p>
<ol>
    <li>One by one, using the Assessments | Add... | Add Assessment for New Client menu item.</li>
    <li>Again, one by one, using the Assessments | Add... | Add Assessment to Existing Client menu item.</li>
    <li>Or in bulk, by uploading a spreadsheet containing client information, using the Assessments | Add... | Bulk Add Clients and Assessments button. When adding assessments in bulk, please use the spreadsheet and format as provided - do not alter the columns and headings.</li>
</ol>
When adding assessments, you must provide client name, surname, email, language of assessment, and type of assessment. You can optionally specify company and project name, as well as a open-ended Other value.


<p>
The project name can be used to group assessments in a single coaching project together - later the project name can be used on the Assessments tab to search for all these associated ssessments.
</p>

<p>
The newly added assessments can be seen on the Assessments page.
</p>

<h3>Guiding and tracking the clients through the assessments</h3>

<p>
Assessment progress can be viewed on the Assessments tab.
</p>

<p>Stages of an assessment:</p>
<ol>
    <li>You invite the client to do the assessment</li>
    <li>Client registers on the assessment (follows the link to the first page of the assessment, fills in some biographical information, and submits the information)</li>
    <li>Client completes the assessment</li>
    <li>The assessment gets scored. For {enneaName} assessments, this is automatic and immediate. {fullName} and {imfName} assessments can take up to five working days to be marked by Aephoria.</li>
    <li>Once the assessments are scored, you send the reports to the client.</li>
</ol>
<p>The Status column on the Assessments table shows these stages as five icons that turn into check marks when the corresponding stage is complete.</p>

<h3>Sending invites to the clients</h3>

<p>In the assessment table, select the assessments for which you want to send out invites, and click the Send Invites button. There are two ways to email an invite:</p>
<ol>
    <li>If you've only selected one assessment, you'll have the option to send from your email address - this will open the invitation email in your default mail client, allow you to review and edit the email, and then send it from your email program. </li>
    <li>You can also choose to send via the Aephoria mail server, in which case you will only get a preview of the invitation email, and it will appear to the client to have been sent from aim@aephoria.co.za. This option is for bulk-sending invitations, and works if you have selected multiple assessments. If you want to edit the email, you will have to edit the template on which it is based.</li>
</ol>


<p>
You can customize the template used for the invitation email by going to the Email Templates page. There are templates for {enneaName}, {imfName}, and {fullName} assessment invitations.
</p>

<h3>Client registration and completion</h3>

<p>
The client will then follow the link in the email invitation, and register and complete the assessment. Registration is the first step of the assessment process (providing biographical details) and completion is typically 10 to 40 minutes after registration (depending on type of assessment and how quickly the client moves through the assessment).
</p>

<p>
{/*FAQ*/} How do I remind someone to start their assessment?
Clients might not take up the invitation. You can then remind them by either resending the link as you did when you sent your invitation or by sending an email directly to them, with the link to the assessment. Get the link for the hesitant client by selecting their assessment and clicking "Copy Assessment Link".
</p>

<p>
{/*FAQ*/} My client hasn't completed the assessment. What are my options?
Sometimes, you might see a client that has registered, but not yet completed. If a long time has elapsed since the client registered, it could indicate the client has abandoned the assessment. You can then choose to also remind the client to continue with the assessment, again by sending the original invitation once more or through a direct message including the assessment link. The client can then follow that link and pick up the assessment from the question where they last left it.
</p>

<h3>Assessment scoring</h3>

<p>
{enneaName} assessment results will be available as soon as the client completes the assessment.
</p>

<p>
    How long does it take to mark a {imfName} assessment once completed? {imfName} results (for {imfName} and Combined assessments) will only be available when Aephoria has scored the Maturity portion of the assessment - this can take up to five working days. Some results marked by our automated marker will be available within 30 minutes of the assessment being completed. Assessments where the client opts out of use of the marking engine, or those sent for human moderation will be available within 5 working days or less. 
</p>

{/* <p>
 How long does it take to mark a {imfName}  assessment once completed?&nbsp;
{imfName} results (for {imfName} and {fullName} assessments) will only be available when Aephoria has scored the Maturity portion of the assessment - this can take up to five working days. You don't need to prompt Aephoria to mark the assessment - the markers are alerted as soon as assessments are completed, and will do the marking as soon as possible.
</p> */}

<p>The marking engine model card is <a onClick={openModelCard} style={{ textDecoration: "underline", cursor: "pointer", color: "blue" }}>here</a></p>
<h3>Feedback to client - reports</h3>

{/*
<p>
FAQ What are the different types of AIM reports? Who do I send which reports?
</p>
*/}

<p>
Aephoria provides the following reports for the client and/or coach:
</p>

<h4>Reports for {enneaName} and {fullName} assessments:</h4>

<ul>
    <li>Enneagram Report</li>
    <li>Enneagram Client Overview</li>
    <li>Enneagram Coach Overview</li>
</ul>

<h4>Reports for {imfName} and {fullName} assessments</h4>

<ul>
    <li>Maturity Report</li>
    <li>Combined Report</li>
    <li>Combined+ Report</li>
    <li>Maturity Overview</li>
</ul>

<p>You can download samples of these reports on the Samples &amp; Brochures tab.</p>

<p>
{/*FAQ*/} How do I send a report to my client? You may choose to email the reports to the client, and/or download a copy of the report for yourself to share later with the client.
</p>

<h4>Download report</h4>

<p>
For a selected assessment, you can download the applicable reports to your computer and then email them to the client yourself, or work through the reports in a feedback session with the client. Do this by selecting the assessment, then clicking the "Download Reports" button on the Assessments tab.
</p>

<h4>Sending reports to clients</h4>

<p>
You can also email the reports to the clients within the portal. Select the assessments for which you want to send reports, and click "Send Reports", and then, as with sending invites, there are two ways to send:
</p>

<ol>
    <li>If you've only selected one assessment, you'll have the option to send from your email address - this will open the report email in your default mail client, allowing you to review the email and even edit it, and then send it from your email program. </li>
    <li>You can also choose to send via the Aephoria mail server, in which case you'll only get a preview of the email, and it will appear to the client to have been sent from aim@aephoria.co.za. This option is for bulk-sending reports for multiple assessments.</li>
</ol>


<p>
The send report email contains a link to a report page, that will allow the client to download the reports you've chosen to send to them.
</p>

<p>
As with the invitation emails, you can customize the template used for the report email by going to the Email Templates page and selecting the "Send Reports" template.
</p>

<h3>Signing up clients for another round of assessments</h3>

<p>
Once a client, or a cohort of clients in a project, have done an assessment, you may want to assign them a further assessment at a later stage. For example, first they did an {enneaName} assessment, and now a {imfName} assessment is called for. Or, initially they did a {fullName} assessment, and now it might be useful for them to redo the Maturity section to assess the effect and return on investment of an intervention.
</p>

<p>This can be done in two ways:</p>

<ol>
    <li>One-by-one: from the assessments page, select the existing client, and click Add... | Add Assessment to Existing Client.</li>
    <li>In bulk: from the assessments page, select Add... | Bulk Add Clients and Assessments and as before, upload the spreadsheet containing client details. Any clients that already exist (based on the email address as key) will have the extra assessment added to them.</li>
</ol>

<h2>Teams</h2>

<p>
A group of clients could be working together in a team. Aephoria offers a set of team reports which offer insight into team dynamics based on the individual assessments of the members.
</p>

<p>The team reports require the following credits:</p>
<ul>
    <li>{enneaTeamName} Team Report: 2 credits</li>
    <li>{imfTeamName} Team Report: 2 credits</li>
    <li>{fullTeamName} Team Report: 4 credits</li>
</ul>


<p>
{/*FAQ*/} How to add assessments to teams?
</p>

<p>
To add assessments to a team, first add a team by clicking on the Teams | Add Team button. Then, you can either:
<ol>
<li>select the team in the table, and click View Team. From there, you can click "Add Members", or </li>
<li>go to the Assessments tab, select the relevant assessments and click Add... | Add to Team.</li>
</ol>
You can add assessments one-by-one to a team - they don't need to be added all at once.
</p>

<p>
Back on the Teams page, you can then select the team, click "View Team" and see the list of assessments. Here you can also specify the team leader, and give comments to each assessment/member.
</p>

<h3>Requesting team reports</h3>

{/*
<p>
FAQ What if a team has no leader?
</p>
*/}

<p>
Once all the assessments in a team have been completed, and you've identified the team leader, you can request Aephoria to generate a team report: from the Teams page, select the team, and click "Request Team Report", selecting the kind of report required ({enneaTeamName}, {imfTeamName}, {fullTeamName}) and specifying a due date.
</p>

<p>
The report will then either become available almost immediately, or Aephoria will then generate the report upload it to your coach portal in a few working days.
</p>

<h2>Credits and costs / balances</h2>

<p>
You have a balance of credits with Aephoria, to be used towards assessments and team reports. See the Balance page for your current balance and the assessment/report costs.
</p>

{/*
<p>
FAQ How to purchase credits?
</p>
*/}

<p>
Please contact <a href="mailto:hello@aephoriagroup.com" target="_blank" rel="noreferrer">Aephoria</a> to purchase more credits. 
</p>

<p>
To qualify for bulk discounts, buy credits in batches before using them. Credits purchased in this way will show as a positive balance in green in the top right corner of your screen. 
</p>


<p>
If you prefer to pay-as-you-go you will not able to qualify for bulk discounts and will receive a single invoice for all credits used at the end of each month for payment within 14-days of the date of invoice. Credits that will be billed at the end of the month will show as a negative balance in red in the top right corner of your screen.
</p>

<h2>Emails and templates</h2>

<p>
As mentioned in the "Sending invites to the clients" and "Sending reports to clients" above, there are always two ways to send emails to clients:
</p>

<ol>
    <li>One-by-one using your own email client program, in which case the client will see the email as being sent from your email address, and you will get a chance to review the email and edit it</li>
    <li>In bulk, with multiple-selected assessments, in which case the emails will be sent from the Aephoria email server, you'll see a preview of a representative sample of the emails but you won't get a chance to edit them, and the emails will be sent from aim@aephoria.co.za</li>
</ol>

{/*
<p>
FAQ What are email templates?
</p>
*/}

<p>
In both cases, the email content is created from templates ({enneaName}, {imfName}, and {fullName} assessment invitation templates, and a send report template).
</p>

<p>
You can customize these templates by going to the Email Templates page.
</p>

<h3>Email troubleshooting</h3>

<p>
{/*FAQ*/} If, when sending from your email address the email window or tab doesn't open - what to do?
You must set your web browser to allow popups on this website. You must also ensure that your email client is set as the handler for the "mailto" protocol, both in your operating system settings and in your browser settings.
</p>

{/*
<p>
FAQ My default email is opening when choosing to send from my mail? XXX is this a FAQ?
</p>

<p>
This is how it should work. 
</p>
*/}

<h2>Deleting assessments</h2>

<p>
If a client has not yet registered for an assessment, you can delete the assessment by selecting it on the Assessments page, and clicking "Delete Assessment".
</p>

<p>
When deleting an unused assessment, you will be credited. Once the client registers for the assessment, you can no longer delete the assessment. You cannot delete assessments that have already been completed. 
</p>

<br></br><br></br>
<br></br><br></br>


<h2 id="whatsnew">What's New</h2>

<br></br><br></br>

<h3>14 October 2026</h3>
<ul>
<li>Email Templates can now be customized for all assessment languages. If you configure the templates in the correct languages, clients will automatically receive their assessment invites and report emails in the language they used to complete the assessment</li>
<li>This help page now contains links to our latest set of tutorial videos - see at the top</li>
</ul>

<h3>22 September 2026</h3>
<ul>
<li>You can share pairs with other coaches with the "Assign SCO" button on the Pairs page, behind the More... button. A coach that receives a pair will be able to access that pair's report.</li>
<li>You can now wrap up / unwrap teams and pairs, in the same way as wrapping Assessments, via the More... button on the Teams and Pairs page</li>
<li>The Accreditation Stamps (accessible in your User Profile under Web Resources) have been refreshed, and you can now embed code in your website that will allow visitors to verify your accreditations with Aephoria</li>
<li>Coach Questionnaire added - you'll see the questions pop up, and you can update your answers at any time in your User Profile, in the section "Get To Know You"</li>
<li>The timezone for all displayed dates / times is shown at the top right</li>
<li>Minor bug fixes and UI tweaks</li>
</ul>

<h3>1 September 2026</h3>
<ul>
<li>Enhanced Maturity Report content has now been made available in the HTML format as well</li>
<li>Sample reports updated</li>
</ul>

<h3>29 July 2026</h3>
<ul>
<li>Enhanced Maturity Report (pdf) for new assessment takers who opt in to AI-assisted customised reports</li>
<li>When bulk adding assessments, import spreadsheet can now contain a project column - values specified there override the umbrella project value provided on the Bulk Add Clients modal</li>
<li>Maturity Overview report shows marking information (AI and/or human)</li>
<li>All emails sent out via the AIM email server (invites, reports) are now HTML with embedded invite/download links.</li>
</ul>

<h3>14 July 2026</h3>
<ul>
    <li>It's now possible to add pairs on the assessment page, even if the assessments are not yet completed.</li>
    <li>It's also now possible to edit a pair's pronouns from the Pairs page - select the pair and click "Edit Pronouns".</li>
    <li>A couple of UI tweaks and minor bug fixes.</li>
</ul>

<h3>17 June 2026</h3>
<ul>
    <li>The new Professional Pairs report is available. See a sample on the Samples & Brochures page.</li>
    <li>The Colleagues Report has been retired. Old reports will still be available via the Pairs page.</li>
    <li>Existing Colleagues Reports can be upgraded to Professional Pairs free of charge - email <a href="mailto:hello@aephoriagroup.com" target="_blank" rel="noreferrer">hello@aephoriagroup.com</a> to request.</li>
</ul>

<h3>22 May 2026</h3>
<ul>
    <li>Your coach-specific client self-registration links have been changed. The old ones are no longer valid. Find the new ones in your User Profile, under Web Resources - Client Self-Registration.</li>
    <li>You can opt into being cc'd on every email sent to clients (via your user profile, under Notification Preferences)</li>
    <li>It's now possible to delete an assistant instead of merely blocking them</li>
    <li>Various UI tweaks and minor bug fixes</li>
</ul>


<h3>24 April 2026</h3>
<ul>
    <li>A new {'{reportlinkonly}'} field for Send Report email templates - the report link with no Aephoria preamble.</li>
</ul>

<h3>10 April 2026</h3>
<ul>
    <li>New Invoices and Orders pages replace the old Balance page.</li>
    <li>There's now a Coach Companion PDF report for Pairs - a one page overview of the pair's stats. Download it from the Pairs table, in the new column "Coach Companion".</li>
</ul>

<h3>4 March 2026</h3>
<ul>
    <li>The Enneagram Report is now available in Spanish.</li>
    <li>A new Combined Overview PDF report is available for Combined assessments - it contains the content of the Ennea Overview and the Maturity Overview PDF reports.</li>
</ul>

<h3>29 January 2026</h3>
<ul>
    <li>Chinese assessment is now available.</li>
</ul>

<h3>9 December 2025</h3>
<ul>
    <li>Clients can now self-register for a Colleagues / Couples Report - for more info, see in your User Profile | Web Resources | Client Self-Registration </li>
    <li>Changes to the Maturity sentence stems - "Love and sex" changed to "Love is" and a new stem added: "Being human in a digital age"</li>
    <li>The Maturity Overview report will indicate if the Aephoria automated checking detected patterns suggesting possible use of a large language model in the sentence completions.</li>
</ul>

<h3>28 October 2025</h3>
<ul>
    <li>The PDF for Pair Reports (Couples, Colleagues) improved.</li>
    <li>You will now be notified by email when a pair report becomes available.</li> 
    <li>Some UI tweaks and minor bug fixes.</li>
</ul>

<h3>2 October 2025</h3>
<ul>
    <li>Fixed issue with layout in Pair HTML reports in the Defences And Lines of Stress section.</li>
</ul>

<h3>2 October 2025</h3>
<ul>
<li>Pair Reports can now be downloaded as PDF, no longer only accessible as HTML. The HTML report is still the recommended way of reading the report on a device. The PDF is meant only for those who want a printed report - it's best to print the PDF rather than printing the HTML report via your browser.</li>
<li>More assessment detail fields have been added to the Dashboard Summary Spreadsheet and the assessments Download Details spreadsheet.</li>
<li>Select All in Assessments now selects across all pages, not only the current page.</li>
<li>For all the tables, the number of records shown per page can be set by the user.</li>
<li>Assessments can now be filtered by wrapped / unwrapped status.</li>
<li>Import Results can now be done for an existing client.</li>
<li>An assistant's email address can now be edited, not only their name and surname.</li>
<li>User Profile shows just a single Save Changes button.</li>
</ul>

<h3>6 August 2025</h3>
<ul>
<li>Minor enhancements to the Enneagram Couples and Colleagues reports.</li>
</ul>

<h3>1 August 2025</h3>
<ul>
<li>Assessments can now be paired together also for Colleagues - so now both the Enneagram Workplace Report and Couples Report are available.</li>
<li>Note: the HTML reports are designed for viewing on your device (mobile, tablet, laptop etc). If you do want to print them out, see the "Report Printing Guidelines" button on the Pairs tab.</li>
</ul>

<h3>18 July 2025</h3>
<ul>
<li>Assessments can now be paired together, for Couples and Colleagues Reports. Enneagram Couples Reports are available now; Enneagram Colleagues Reports coming soon.</li>
<li>Create a pairing by selecting two completed Enneagram or Combined Assessments on the Assessments tab, then click "Add..." and "Add Pairs". See the paired couple on the new Pairs tab.</li>
<li>A sample of the new HTML Couples Report is available on the Samples & Brochures tab.</li>
<li>Note: if you want to print the Couples Report, be sure to switch on "Background graphics" in your browser / system print options dialog.</li>
</ul>

<h3>25 June 2025</h3>
<ul>
<li>The Team Report (Maturity and Combined) now includes two new pages - Stages of Maturity overview with member names, and a Maturation Journey graphic. </li>
<li>Your web profile on aephoriagroup.com can now include a link to your website - set it in your user profile (click on your name in the top-left corner of the portal and find the Web Practitioner Profile section)</li>
<li>Enneagram Sample Reports available in Afrikaans, Hungarian, Italian, Slovak.</li>
<li>Import Assessment has been separated from Add Assessment for clarity.</li>
<li>Some UI tweaks and minor bug fixes.</li>
</ul>

<h3>26 May 2025</h3>
<ul>
<li>The Enneagram Report is now available in Italian.</li>
<li>The Enneagram Report icons and various panel backgrounds are no longer coloured according to client's ennea - rather the standard Enneagram Report colour is used.</li>
<li>Enneagram colours updated in individual HTML and PDF reports, and Enneagram and Maturity colours in team PDF and Powerpoint reports.</li>
<li>Adding many members to a team has been speeded up.</li>
</ul>

<h3>7 May 2025</h3>
<ul>
<li>The Enneagram Report is now available in Afrikaans.</li>
{/*<li>Updated the one-page Ennea Overview report.</li>*/}
<li>Allowing for emojis in any user input.</li>
<li>Some UI tweaks and minor bug fixes.</li>
<li>General system upgrade.</li>
</ul>

<h3>2 April 2025</h3>
<ul>
<li>Report content tweaked.</li>
<li>Some UI tweaks and minor bug fixes.</li>
</ul>

<h3>13 March 2025</h3>
<ul>
<li>Launch of client self-register feature. On the Balance tab, you can find a link that you can provide to clients for assessment self-registration.</li>
<li>The Team Report maturity and enneagram graphics have been updated to align them with the new individual report graphics.</li>
<li>You can now leave a message for the Aephoria dev team directly from within the portal, to report a bug or a frustration, or share an idea, request a marketing link, etc.</li>
<li>When adding/editing assessments, the project can now now be selected from a dropdown list of all your previous projects, to facilitate greater consistency.</li>
<li>Some UI tweaks and minor bug fixes.</li>
</ul>

<h3>24 February 2025</h3>
<ul>
<li>The 1 March price increase is shown on the Balance tab</li>
</ul>

<h3>3 February 2025</h3>
<ul>
<li>Fixed typo in Enneagram report content.</li>
</ul>

<h3>14 January 2025</h3>
<ul>
<li>Trifix is now shown consistently in client result order, max to min, not in gut-heart-head order, in all PDF and HTML reports.</li>
<li>Fixed text-only HTML reports for Enneagram-only and Maturity-only assessments.</li>
</ul>

<h3>16 December 2024</h3>
<ul>
<li>You can share teams with other coaches with the "Assign SCO" button on the Teams page. A coach that receives a team will be able to access that team's report.</li>
<li>The Aephoria Brochure on Samples & Brochures has been updated.</li>
<li>Fixed session expiry while downloading many reports.</li>
<li>Some UI tweaks and minor bug fixes.</li>
</ul>

<h3>14 November 2024</h3>
<ul>
<li>HTML reports have been added for the assessments, as another way of presenting the content in the four corresponding PDF reports. On the Assessments table, there's a new R icon for you to access the assessment's HTML report/reports. Also, when sending reports to clients via the system, the client will be able to access the HTML reports, as well as (as before) download the PDF reports</li>
<li>The Enneagram HTML report is also available as an only-text version, for better accessibility.</li>
<li>PDF reports have been updated, with some new graphics, icons, headings, and tweaked content.</li>
<li>The IMf assessment has been renamed to "Aephoria Maturity Assessment".</li> 
<li>The full assessment has been renamed to "Aephoria Combined Assessment".</li> 
<li>The name "AIM" has been retired, and any references to AIM should be replaced with "Aephoria". If you have customized your email templates, please update them to reflect these changes.</li>
<li>Optional two-factor authentication is now available. If you want two-factor authentication when you log in, then enable it from your user profile (click on your name in the top-left corner of the portal and find the Two-factor Authentication section).</li>
<li>The Help above has been revised, to align it with recent changes, and the tutorial video has been updated.</li>
<li>Some UI tweaks, validations, and minor bug fixes.</li>
</ul>

<h3>7 October 2024</h3>
<ul>
<li>Assessments are now available in Italian.</li>
<li>Accreditation stamps can be downloaded from your user profile (click on your name in the top-left corner of the portal and find the Stamps section).</li>
<li>The combined (Enneagram + Maturity) team report name has changed from "AIM" to "COMBINED".</li>
<li>Cover page logo and "AIM" branding has been changed for Ennea, Maturity, Foundation, Extended, Teams reports.</li>
<li>The "Foundation" and "Extended" Report names have changed to "Combined" and "Combined+" respectively.</li>
<li>On Ennea Overview and Ennea Coach Overview reports, there's an indicator to show if the assessment results were imported.</li>
<li>On the Samples & Brochures page, the brochure has been updated.</li>
<li>Some UI tweaks and minor bug fixes.</li>
</ul>    

<h3>20 September 2024</h3>
<ul>
<li>Download details of selected assessments in a spreadsheet, via the Assessments tab, More... | Download Details</li>
<li>Fixed bulk upload warnings and check bulk upload spreadsheet for column headers</li>
<li>Policy signing label and preamble tweaks</li>
</ul>    

<h3>3 September 2024</h3>
<ul>
    <li>Sign / accept the Data Processing Agreement and Coach Portal Service Terms, and view them (as well as General Terms and the EULA) from your user profile page.</li>
    <li>Assessments are now available in Zulu.</li>
    <li>Mini-assessment reports have received further styling.</li>
    <li>Some UI tweaks and optimizations.</li>
</ul>

<h3>16 August 2024</h3>
<ul>
<li>Teams - you can now add team members directly from the Team page (no longer necessary to go to the Assessments tab to do this).</li>
<li>Some visual tweaks, usability changes, and added validations.</li>
<li>Enneagram Coach Overview now shows countertype and r-line/s-line.</li>
<li>Reports: the 15th IMf question is now reported.</li>
<li>Reports: a few small typos fixed.</li>
</ul>

<h3>18 July 2024</h3>
<ul>
<li>Dashboard - Download summary spreadsheet - the spreadsheet, in the Assessments sheet, now includes assessment results.</li>
<li>Teams: editing of teams (add and delete members or change leaders) is now blocked, if the team has a report requested or issued. Rather duplicate the team and edit its members and then request a new report.</li>
<li>Some visual tweaks and added validations.</li>
</ul>

<h3>10 July 2024</h3>
<ul>
<li>Bulk upload - show detailed errors for missing name, surname, email or invalid email.</li>
<li>Adding clients/assessments is now SCO aware.</li>
<li>Assessments tab mobile view - name, email as a single tile, with ennea and IMf result columns added.</li>
<li>A few visual fixes.</li>
<li>Download Summary Spreadsheet - transactions sheet now includes email domain of assessments added/deleted.</li>
</ul>

<h3>27 May 2024</h3>
<ul>
<li>The teams page is now available on mobile, so you can download team reports while away from your desktop.</li>
</ul>

<h3>21 May 2024</h3>
<ul>
<li>When adding new client, new assessment, and bulk adding assessments, you can, as before, select reports for auto-sending to client after completion of assessment (the ennea-only reports) and also, now newly added, choose which reports to auto-send to client after marking (the maturity-containing reports).</li>
<li>View assessment details - for one assessment directly with the new info icon, and for one or more assessments via the More... | View Details button.</li>
<li>Updated sample reports for the new ennea overview and coach ennea overview.</li>
<li>On bulk spreadsheet upload, there's now a check if any clients already exist - if they do, then you must first confirm before the bulk add can go ahead.</li>
<li>When editing assessments, you can now change the auto-send reports options.</li>
<li>When you copy an assessment link to clipboard, you'll see a message about the implications of this - that it marks the assessment as invited.</li>
<li>On the Team page, there's a warning that report generation can take up to two working days, especially for larger teams.</li>
<li>A missing word ('conversations') on Challenges on p5 has been fixed in the Team Report for 937 teams.</li>
</ul>

<h3>24 April 2024</h3>
<ul>
    <li>The Enneagram Coach Overview report has been extended in content and compressed in length.</li>
    <li>The Enneagram Overview report has been slightly tweaked.</li>
    <li>Dashboard facelift.</li>
    <li>The Wrap/Unwrap assessment functionality is now more user-friendly.</li>
    <li>Fixed minor navigation issue.</li>
</ul>

<h3>2 April 2024</h3>
<ul>
    <li>The Coach Portal has been restyled - let us know what you think!</li>
    <li>The old Clients tab has been removed. All associated functionality can now be accessed on the Assessments tab.</li>
    <li>The Maturity, Foundation, and Extended Reports now show, in the "What is Identity Maturation" section, the client's range on the general IMf shell image.</li>
    <li>Imported assessments can now be deleted.</li>
</ul>


<h3>13 March 2024</h3>
<ul>
    <li>Assessments are now available in Afrikaans.</li>
</ul>

<h3>1 March 2024</h3>
<ul>
    <li>The Foundation Report has been extended, with extra IMf leadership content, and with Ennea and IMf graphed results in a new Appendix A.</li>
    <li>The report covers have been refreshed, and the Basic Reports are no longer called "Basic".</li>
    <li>A few small typos and imperfections have been fixed in report content.</li>
    <li>Note: for the Team Report, the new cover and tweaks in content will only be present in new reports requested. If you wish to have a specific old report refreshed, please let us know.</li>
    <li>The Assessments table now shows the ordered trifix instead of the gut-heart-head trifix.</li>
    <li>Reverted to previous Send Reports behaviour, so that reports are sent to client without asking for an override email address.</li>
    <li>An issue that involved notifications for bulk added assessments has been resolved.</li>
    <li>Other small changes and fixes.</li>
</ul>

<h3>12 December 2023</h3>
<ul>
    <li>You can now import Enneagram results for a client - on the Clients tab, select a client, and then click "Import Results".</li>
    <li>You can now add a new client without having to simultaneously add an assessment for them.</li>
    <li>You can also combine two assessments for a client (for example if they've done a retest for Enneagram to combine it with their maturity results in a full assessment). On the Assessments tab, select two assessments of the same client, and click "Combine".</li>
    <li>The Assessments table now shows if an assessment is a result of the Import Results process, or a combination of previous assessments, or if it was used in (parented) a combination.</li>
    <li>Assessment-takers can now self-select the assessment language from the list of available languages. You can still however pre-set a language for the assessment.</li>
    <li>Assessments can now be done in the Tsonga language.</li>
</ul>

<h3>20 November 2023</h3>
<ul>
    <li>Assistants - you can now add an Assistant profile to help you administer assessments. Begin the process by going to the Assistants section in your profile (click on your name at the top left).</li>
    <li>When adding assessments, you no longer have the option of overriding the default notification settings in your profile - go there if you want to change your notification preferences.</li>
    <li>Minor fixes and speed optimizations.</li>
</ul>

<h3>23 October 2023</h3>
<ul>
    <li>Secondary Coach Ownership functionality added.</li>
    <li>An issue with the Team Report PPTX opened in Keynote has been fixed.</li>
</ul>

<h3>11 October 2023</h3>
<ul>
    <li>The Coach Portal is now available on mobile, with limited functionality - meant to help in urgent on-the-go situations.</li>
    <li>When adding Clients, a new field "Other" has been added, which can, for example be filled with a client identification number.</li>
    <li>From the Dashboard, you can download a spreadsheet which includes all your assessments, teams, and your transaction history.</li>
    <li>Your transaction history is shown on the Balance tab. Transactions from this date onwards will include more details in their description; old transactions show a basic description.</li>
    <li>All delete functionality now works on multiple selected records.</li>
    <li>Assessments can now also be offered in Sesotho, Dutch and Portuguese.</li>
    <li>Fixed issue of editing of a team's project.</li>
</ul>

<h3>24 August 2023</h3>
<ul>
    <li>When adding assessments or clients, you can now choose to have Enneagram-related reports auto-sent to the client on completion of the assessment.</li>
    <li>On the Assessments table, in the Status column, for new assessments, there is now tracking to show reports sent to client and downloaded by client. Old assessments still just have a single tick for reports sent. But, for new reports: Ennea and Maturity (IMf) assessments have a single report tracking icon. Full assessments have two icons, one for Ennea reports and one for IMf-containing reports. The icons move through these stages: reports not sent (empty circle), reports sent but not downloaded by client (circle with dot inside), reports downloaded by client (circle with tick inside). It's enough for just one report to be sent or one report to be downloaded for the icon to move to the next stage; not all applicable reports need to be sent or downloaded.</li>
    <li>Assessments are now available in Hungarian and Slovak. The Basic Enneagram Report is also available in these two languages. If you send the Basic Enneagram Report to a client that has done the assessment in one of these languages, the client will automatically be offered both the English and Hungarian or Slovak report for download.</li>
</ul>

<h3>1 August 2023</h3>
<ul>
    <li>You can choose to be listed as a practitioner on the Aephoria <a target="_blank" rel="noreferrer" href="https://aephoriagroup.com/practitioners/">website</a>, or update your profile if it is already visible on the site. To do so, go to your profile in this portal (click on your name at the top left), and see the Web Practitioner Profile section.</li>
    <li>When duplicating teams, you can now make minor changes (e.g. remove a person or add a person to the team) free of charge.</li>
    <li>You can download an AIM brochure for clients in the same place as the sample reports (Samples &amp; Brochures tab)</li>
</ul>

<h3>14 June 2023</h3>
<ul>
    <li>Teams can be duplicated with one click, on the Teams tab.</li>
    <li>The assessment language can now be specified - Xhosa, Spanish, French, German, or English.</li>
    <li>The invite history for each assessment can now be viewed. To view, in the assessments table, click on the Invite Sent ticks in the Status column.</li>
    <li>When sending invites for previously invited assessments, REMINDER is prefixed to the email subject, and the Status icon shows as double-ticks.</li>
    <li>Copying an assessment link is now treated as an invite, and the Status icon is updated accordingly.</li>
</ul>


<h3>17 May 2023</h3>
<ul>
    <li>Team reports are now supplied in both PowerPoint and PDF format.</li>
    <li>Reports can be downloaded for multiple selected assessments.</li>
    <li>Secure password criteria are enforced when changing password in Profile or Forgotten Password.</li>
    <li>Other small changes and fixes.</li>
</ul>

<h3>19 April 2023</h3>
<ul>
    <li>When viewing a team, columns have been added to the team member table, showing company, position, completion date, Ennea and IMf scores.</li>
    <li>Assessments can now be edited, limited to just changing the project for now.</li>
    <li>The text when ordering credits in bulk has been updated.</li>
    <li>Some other small changes and fixes.</li>
</ul>

<h3>15 March 2023</h3>
<ul>
    <li>You can now bulk order credits from the "Balances" tab.</li>
    <li>The sorting, searching, and filtering functionality on the Clients, Assessments, and Teams table has been improved.</li>
    <li>When sending invites and reports via the AIM server (not your own email client) a preview of the emails to be sent is now shown, for you to check before sending.</li>
    <li>Team reports in most cases will now be available almost immediately after requesting them.</li>
    <li>The sample reports have been updated.</li>
</ul>

<h3>16 November 2022</h3>
<ul>
    <li>New versions of all the reports are now available.</li>
</ul>

<h3>16 August 2022</h3>
<ul>
    <li>Team reports are now requested on a team's detail page - on the Teams page, click "View Team" to see team members and request the team report.</li>
    <li>The filter on the Assessments page now has a Show All option.</li>
    <li>Several functionality issues have been addressed, including issues when selecting and working with multiple assessments at once.</li>
</ul>

<h3>26 May 2022</h3>
<ul>
    <li>New "Wrap Up" button on Assessments. An assessment marked as wrapped up is excluded from the Dashboard pipeline summaries and from the Assessment pipeline filter results, and is shown in gray in the assessments table. The new button toggles the Wrapped Up flag.</li>
    <li>In the Assessments table, in the Status column, the marked icon for Enneagram assessments is only ticked when the assessment has been completed. Similarly, the Ennea Results column only shows a value when the assessment has been completed, no longer showing partial results for an assessment in progress.</li>
    <li>Popup dialogs, validations, and feedback messages have been tweaked.</li>
</ul>

<h3>28 April 2022</h3>
<ul>
    <li>Dashboard now shows the number of assessments in each of the five different pipeline stages:
        <ol>
            <li>not invited yet</li>
            <li>invited but not started</li>
            <li>started but not completed</li>
            <li>completed and in marking</li>
            <li>completed with reports to send</li>
        </ol>
    </li>
    <li>Assessments table can now be filtered on the five different pipeline stages</li>
    <li>You can specify multiple recipients for notifications - do so in your profile (click on your name at the top left to get there)</li>
    <li>The report sent history (which reports, to whom, when, and from which email server) for each assessment can now be viewed. To view, in the assessments table, click on the Report Sent ticks in the status column.</li>
    <li>You can download a team summary spreadsheet for each team, giving the details and assessment results of all the members in the team. On the teams page, select a team, then click on the "Download Summary" button.</li>
    <li>You can revert your email templates to the defaults provided by Aephoria.</li>
    <li>You can reset your password if you forget it. Start the process by clicking on the "Forgot password?" link on the login screen.</li>
</ul>

<h3>29 March 2022</h3>
<ul>
    <li>New messages page, showing messages from Aephoria regarding the Coach Portal</li>
    <li>You can now choose to receive email notifications when a team report is ready, when a client has finished an Enneagram assessment, and when a client's IMf or full assessment has been marked</li>
    <ul>
        <li>In your profile, set up default notification settings</li>
        <li>When adding a client or assessment, or bulk adding clients, you can choose to override those default settings for the specific assessments being added</li>
    </ul>
</ul>

<h3>17 March 2022</h3>
<ul>
    <li>First general release</li>
</ul>


</div>
);
};



export default helpText;













